import React, { useState, useEffect } from 'react';
import { Settings, X } from 'lucide-react';

const SettingsModal = ({
  showSettings,
  setShowSettings
}) => {
  // Local state for settings
  const [autoEnterEnabled, setAutoEnterEnabled] = useState(true);
  const [debounceEnabled, setDebounceEnabled] = useState(true);
  const [keyboardType, setKeyboardType] = useState('numeric');

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('scannerSettings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        setAutoEnterEnabled(settings.autoEnterEnabled ?? true);
        setDebounceEnabled(settings.debounceEnabled ?? true);
        setKeyboardType(settings.keyboardType ?? 'numeric');
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    }
  }, []);

  // Save settings to localStorage when settings change
  useEffect(() => {
    const settings = {
      autoEnterEnabled,
      debounceEnabled,
      keyboardType
    };
    
    localStorage.setItem('scannerSettings', JSON.stringify(settings));
  }, [autoEnterEnabled, debounceEnabled, keyboardType]);
  if (!showSettings) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-96 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="text-blue-600" size={24} />
              Scanner Settings
            </h2>
            <button
              onClick={() => setShowSettings(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Auto Enter Setting */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Auto Enter</h3>
                <p className="text-sm text-gray-600">
                  Automatically submit barcode after 300ms delay
                </p>
              </div>
              <button
                onClick={() => setAutoEnterEnabled(!autoEnterEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${autoEnterEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${autoEnterEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>

            {/* Duplicate Scan Protection */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Duplicate Scan Protection</h3>
                <p className="text-sm text-gray-600">
                  Prevents scanning the same barcode within 2 seconds
                </p>
              </div>
              <button
                onClick={() => setDebounceEnabled(!debounceEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${debounceEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${debounceEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>

            {/* Keyboard Type Setting */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Keyboard Type</h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="keyboardType"
                    value="numeric"
                    checked={keyboardType === 'numeric'}
                    onChange={(e) => setKeyboardType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Numeric (0-9 only)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio" name="keyboardType"
                    value="tel"
                    checked={keyboardType === 'tel'}
                    onChange={(e) => setKeyboardType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Telephone (0-9 + symbols)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="keyboardType"
                    value="text"
                    checked={keyboardType === 'text'}
                    onChange={(e) => setKeyboardType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Full Text (QWERTY)</span>
                </label>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Scanner Tips:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Use Enter key to manually submit barcode</li>
                  <li>When auto-enter is off, use the scan button</li>
                  <li>Scan rack barcode first, then shirt barcodes</li>
                  <li>Numeric keyboard is recommended for barcodes</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setShowSettings(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;